import React, { useState } from 'react'

import { Popover } from '@components/popover'
import {
  useBirthdayCancellation,
  useCancelBirthdayOrder,
} from '@hooks/useBirthdayCancellation'

import {
  CancellationActions,
  CancellationContent,
  LoadingMessage,
  OrderMeta,
  OrderRow,
  OrderSection,
  OrderTotal,
  ReasonField,
  SelectionLink,
  StatusMessage,
} from './birthdayCancellation.styled'

const formatMoney = (value: number) =>
  `${new Intl.NumberFormat('cs-CZ').format(value)} Kč`

export const BirthdayCancellation = ({
  token,
  onClose,
}: {
  token: string
  onClose: () => void
}) => {
  const cancellation = useBirthdayCancellation(token)
  const cancelOrder = useCancelBirthdayOrder(token)
  const [reason, setReason] = useState('')
  const [cancelled, setCancelled] = useState(false)
  const [cancelFailed, setCancelFailed] = useState(false)
  const [emailSent, setEmailSent] = useState(false)
  const [emailFailed, setEmailFailed] = useState(false)

  const order = cancellation.data
  const isCancelled = cancelled || order?.status === 'cancelled'
  const needsEmail = isCancelled && !(emailSent || order?.cancellationEmailSent)
  const submit = async () => {
    setCancelFailed(false)
    setEmailFailed(false)
    try {
      const result = await cancelOrder.mutateAsync({ reason: reason.trim() })
      setCancelled(true)
      setEmailSent(result.confirmationEmailSent)
      setEmailFailed(!result.confirmationEmailSent)
    } catch {
      setCancelFailed(true)
    }
  }

  return (
    <Popover
      open
      title="Zrušení narozeninové oslavy"
      description="Zkontrolujte celou objednávku a potvrďte její zrušení."
      onClose={onClose}
    >
      <CancellationContent>
        {cancellation.isLoading && (
          <LoadingMessage>Načítáme objednávku…</LoadingMessage>
        )}
        {(cancellation.isError || (!cancellation.isLoading && !order)) && (
          <StatusMessage $error role="alert">
            Odkaz pro zrušení není platný nebo objednávku nelze načíst.
          </StatusMessage>
        )}
        {order && (
          <>
            <OrderMeta>
              <span>Jméno</span>
              <strong>{order.customerName}</strong>
              <span>Datum</span>
              <strong>
                {new Date(`${order.date}T12:00:00`).toLocaleDateString('cs-CZ')}
              </strong>
            </OrderMeta>
            <OrderSection>
              <h3>Rezervované hry</h3>
              {order.games.map((game) => (
                <OrderRow key={`${game.name}-${game.time}`}>
                  <span>
                    {game.name} · {game.time} · {game.players} hráčů
                  </span>
                  <span>{formatMoney(game.price)}</span>
                </OrderRow>
              ))}
            </OrderSection>
            {order.food.length > 0 && (
              <OrderSection>
                <h3>Občerstvení</h3>
                {order.food.map((item) => (
                  <OrderRow key={item.name}>
                    <span>
                      {item.name} × {item.quantity}
                    </span>
                    <span>{formatMoney(item.total)}</span>
                  </OrderRow>
                ))}
              </OrderSection>
            )}
            {(order.services?.length ?? 0) > 0 && (
              <OrderSection>
                <h3>Služby</h3>
                {order.services?.map((item) => (
                  <OrderRow key={item.name}>
                    <span>
                      {item.name} × {item.quantity}
                    </span>
                    <span>{formatMoney(item.total)}</span>
                  </OrderRow>
                ))}
              </OrderSection>
            )}
            <OrderTotal>Celkem {formatMoney(order.total)}</OrderTotal>
            {isCancelled ? (
              <>
                <StatusMessage role="status">
                  Celá objednávka byla zrušena. Všechny její herní rezervace
                  jsou nyní uvolněné.
                </StatusMessage>
                {emailFailed && (
                  <StatusMessage $error role="alert">
                    Potvrzovací e-mail se nepodařilo odeslat. Můžete to zkusit
                    znovu.
                  </StatusMessage>
                )}
              </>
            ) : (
              <>
                <ReasonField>
                  Důvod zrušení
                  <textarea
                    rows={3}
                    maxLength={1000}
                    value={reason}
                    onChange={(event) => setReason(event.target.value)}
                  />
                </ReasonField>
                {(cancelOrder.isError || cancelFailed) && (
                  <StatusMessage $error role="alert">
                    Objednávku se nepodařilo zrušit. Zkuste to prosím znovu nebo
                    kontaktujte Questerland.
                  </StatusMessage>
                )}
              </>
            )}
            <SelectionLink href={order.selectionUrl}>
              Zobrazit původní výběr
            </SelectionLink>
          </>
        )}
        <CancellationActions>
          <button type="button" onClick={onClose}>
            {isCancelled ? 'Zavřít' : 'Ponechat rezervaci'}
          </button>
          {order && (!isCancelled || needsEmail) && (
            <button
              type="button"
              disabled={cancelOrder.isLoading}
              onClick={submit}
            >
              {cancelOrder.isLoading
                ? 'Odesíláme…'
                : needsEmail
                ? 'Odeslat potvrzení e-mailem'
                : 'Zrušit rezervaci'}
            </button>
          )}
        </CancellationActions>
      </CancellationContent>
    </Popover>
  )
}
